import * as yup from "yup";
import {useFormik} from "formik";
import {defaultPreregistrationFields} from "../components/app/customizePreregistration/defaultFields";
import {useTranslation} from "react-i18next";
import {EXHIBITION} from "../locales/components/namespaces";
import {useMemo} from "react";
import _ from 'lodash'
import {findCountryCode} from "../helpers/helpers";
import {CatalogueLanguages} from "../helpers/constants";

export const usePreregistrationForm = ({
                                           exhibition,
                                           countries,
                                           interests,
                                           categories,
                                           phoneCodes,
                                           onSubmit,
                                           preregistrationFormValues
                                       }) => {
    const {t} = useTranslation(EXHIBITION)

    const exhibitionPreregistrationFields = useMemo(() => exhibition && exhibition
        .preregistrationFormFields ? exhibition
        .preregistrationFormFields : [], [exhibition])

    const validationSchema = yup.object({
        fieldValues: yup.object(
            exhibitionPreregistrationFields
                .reduce((p, n) => {
                    if (!(n.enabled)) return p
                    if (n.type === "surname") return p
                    return {
                        ...p,
                        [n.id]: (
                            n.type === "name" ? (exhibition?.catalogueLanguage === CatalogueLanguages.EN ? yup.string().required(t('required')).matches(/^([A-Za-z^!@#$%&*()_+\-\\|{}[\].,<>?'":;/`=\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s\w]*)$/gi, t('Only latin and special characters allowed.')) : yup.string().required(t('required'))) :
                                n.type === "companyName" || n.type === "address" || n.type === "postalCode" || n.type === "vat" || n.type === 'position' ? (exhibition?.catalogueLanguage === CatalogueLanguages.EN ? yup.string().when(["isIndividual"], {
                                        is: (isIndividual) => isIndividual !== true && n.required === true,
                                        then: (schema) => schema.notRequired(t('required')),
                                        otherwise: (schema) => schema.notRequired(),
                                    }).matches(/^([A-Za-z^!@#$%&*()_+\-\\|{}[\].,<>?'":;/`=\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s\w]*)$/gi, t('Only latin and special characters allowed.')) : yup.string().when(["isIndividual"], {
                                        is: (isIndividual) => isIndividual !== true && n.required === true,
                                        then: (schema) => schema.notRequired(t('required')),
                                        otherwise: (schema) => schema.notRequired(),
                                    })) :
                                    n.type === "city" || n.type === "companyWebsite" || n.type === 'instagram' || n.type === 'facebook' ? yup.string().when(["isIndividual"], {
                                            is: (isIndividual) => isIndividual !== true && n.required === true,
                                            then: (schema) => schema.notRequired(t('required')),
                                            otherwise: (schema) => schema.notRequired(),
                                        }) :
                                        n.type === "email" ? yup.string().when(["isIndividual"], {
                                                is: (isIndividual) => isIndividual !== true && n.required === true,
                                                then: (schema) => schema.email(t('Enter a valid email')).notRequired(t('required')),
                                                otherwise: (schema) => schema.email(t('Enter a valid email')).notRequired(),
                                            }) :
                                            n.type === "mobile" || n.type === "tel" ?
                                                yup.number().positive(t('Enter a valid phone between 8 and 15 digits')).integer(t('Enter a valid phone between 8 and 15 digits')).test(
                                                    'Enter a valid phone between 8 and 15 digits',
                                                    t('Enter a valid phone between 8 and 15 digits'),
                                                    function (value) {
                                                        if (this.parent['isIndividual'] !== true && n.required === true) {
                                                            return value ? String(value).length >= 8 && String(value).length <= 15 : true
                                                        } else {
                                                            return value ? String(value).length >= 8 && String(value).length <= 15 : true
                                                        }
                                                    }
                                                ) :
                                                n.type === "personCount" ?
                                                    yup.number().when(["isIndividual"], {
                                                        is: (isIndividual) => isIndividual !== true && n.required === true,
                                                        then: (schema) => schema.positive().integer().notRequired(t('required')),
                                                        otherwise: (schema) => schema.positive().integer().notRequired(),
                                                    }) :
                                                    n.type === "isIndividual" ?
                                                        yup.mixed().test(
                                                            'acceptedValues', t('Field must be checked'), function (value) {
                                                                return n.required !== true;
                                                            }
                                                        ) :
                                                        n.type === "hasRevisited" ?
                                                            yup.bool().oneOf([true, false], t('Field must be checked')) :
                                                            n.type === "country" ? yup.mixed().test(
                                                                    'required', t('required'), function (value) {
                                                                        if (n.required === true) {
                                                                            return value && value?.id
                                                                        } else {
                                                                            return true
                                                                        }
                                                                    }
                                                                ) :
                                                                n.type === "characterization" ? yup.mixed().test(
                                                                        'required', t('required'), function (value) {
                                                                            if (n.required === true) {
                                                                                return true
                                                                            } else {
                                                                                return true
                                                                            }
                                                                        }
                                                                    ) :
                                                                    n.type === "interests" ? (n.multiple === true ? yup.mixed().test(
                                                                            'required', t('Select at least one'), function (value) {
                                                                                if (this.parent['isIndividual'] === false && n.required === true) {
                                                                                    return true
                                                                                } else {
                                                                                    return true
                                                                                }
                                                                            }) : yup.mixed().test(
                                                                            'required', t('Select only one'), function (value) {
                                                                                if (this.parent['isIndividual'] === false && n.required === true) {
                                                                                  return true
                                                                                } else {
                                                                                    return true
                                                                                }
                                                                            })) :
                                                                        yup.string().when(["isIndividual"], {
                                                                            is: (isIndividual) => isIndividual !== true && n.required === true,
                                                                            then: (schema) => schema.notRequired(t('required')),
                                                                            otherwise: (schema) => schema.notRequired(),
                                                                        })
                        )
                    }
                }, {}))
    })
    
    /*
     * This useMemo builds a fresh, merged list of form field definitions without mutating the originals.
     * It takes the default field list and the exhibition-specific field list, merges them by id, then sorts the final array by weight
     */
    const formFields = useMemo(() => {
        // Create a lookup object from the default field list.
        // Key = field.id, value = a deep-cloned copy of that field.
        // Deep-cloning prevents later mutations from affecting the original defaults.
        const defaultById = Object.fromEntries(
            defaultPreregistrationFields.map(field => [field.id, structuredClone(field)])
        );
        // Create a second lookup object from the exhibition-specific field list.
        // If there are no exhibition fields yet, use an empty array.
        // Again, deep-clone each field so the source data stays untouched.
        const exhibitionById = Object.fromEntries(
            (exhibitionPreregistrationFields || []).map(field => [field.id, structuredClone(field)])
        );
        // Collect every unique field id from both lookup objects.
        // This ensures we keep defaults that are not overridden,
        // and also include any exhibition-only fields.
        const allIds = Array.from(
            new Set([...Object.keys(defaultById), ...Object.keys(exhibitionById)])
        );
        // Build the final array of fields.
        // For each id:
        // 1. Start with the default field object, if it exists.
        // 2. Overlay the exhibition-specific field object on top of it.
        // 3. The exhibition field wins when both define the same property.
        const result = allIds.map(id => ({
            ...(defaultById[id] || {}),
            ...(exhibitionById[id] || {}),
        }));
        // Sort the final merged list by weight ascending.
        // This controls the display order of fields in the form.
        return _.orderBy(result, 'weight', 'asc');
    }, [exhibitionPreregistrationFields]);

    const formValues = useMemo(() => {
        return defaultPreregistrationFields
            .reduce((p, n) => {
                let initialValue = ''
                if (n.inputType === 'checkbox') initialValue = false
                if (n.id === 'interests') initialValue = []
                if (n.id === 'mobilePhone') initialValue = 30
                return {
                    [n.id]: initialValue,
                    ...p,
                }
            }, {
                ...preregistrationFormValues,
                characterization: preregistrationFormValues?.characterization && categories.length > 0 ? categories.find(f => f.id === preregistrationFormValues.characterization) : '',
                interests: interests?.filter(f => preregistrationFormValues.interests.includes(f.id)),
                countries: typeof preregistrationFormValues?.countries === 'string' && preregistrationFormValues?.countries && countries && countries.length > 0 ? countries.find(f => f.id === preregistrationFormValues.countries) : preregistrationFormValues?.countries,
                countryPhoneCode: findCountryCode(preregistrationFormValues?.countryPhoneCode, preregistrationFormValues?.mobilePhone, phoneCodes)
            } ?? {})
    }, [categories, countries, interests, phoneCodes, preregistrationFormValues])

    const formik = useFormik({
        initialValues: {
            exhibitionId: exhibition?.id,
            fields: formFields,
            fieldValues: formValues
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: onSubmit,
    })

    const fieldValues = useMemo(() => (formik.values.fieldValues), [formik.values.fieldValues])

    return {
        formik,
        fieldValues,
        fields: formFields,
        exhibitionPreregistrationFields
    }
}
